<template>
  <div class="order">
    <template v-if="!isLoadingOrder && !orderNotFound">
      <v-container>
        <v-row justify="center" align="center">
          <v-col cols="auto">
            <v-card color="white" class="pb-2" light elevation="2" max-width="700">
              <v-subheader>Order Details</v-subheader>

              <v-container class="mt-0" fluid>
                <v-row>
                  <v-col class="py-0" cols="12">
                    <span class="body-2">Name: {{ order.name }}</span>
                  </v-col>
                  <v-col class="py-0" cols="12">
                    <span class="body-2">Order ID: {{ order.id }}</span>
                  </v-col>
                  <v-col class="py-0" cols="12">
                    <span class="body-2">Attendant: {{ order.attendant }}</span>
                  </v-col>
                  <v-col v-show="order.client_phone" class="py-0" cols="12">
                    <span class="body-2">Client Phone: {{ order.client_phone }}</span>
                  </v-col>
                  <v-col class="py-0" cols="12">
                    <span class="body-2">Created: {{ order.created }}</span>
                  </v-col>
                  <v-col class="py-0" cols="12">
                    <span class="body-2">Modified: {{ order.modified }}</span>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="py-0">
                    <v-row>
                      <v-col class="py-0 px-0 text-center font-weight-bold" offset="9">GH₵</v-col>
                    </v-row>

                    <v-row>
                      <v-col class="py-0 body-2" cols="9">Amount Payable</v-col>
                      <v-col class="py-0 px-0 text-center" cols="3">
                        {{
                        order.amount_payable
                        }}
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>

              <div class="d-flex mr-4 mb-3">
                <v-btn
                  depressed
                  small
                  class="primary darken-4 text-none mb-2 ml-2"
                  @click="addCartProducts()"
                  :loading="isAddingItems"
                >
                  <v-icon>mdi-cart-arrow-right</v-icon>Order Cart
                </v-btn>

                <v-spacer></v-spacer>

                <v-fade-transition leave-absolute>
                  <template v-if="pendingOrder">
                    <v-btn
                      :disabled="isStaff"
                      @click="unsetPendingOrder()"
                      icon
                      :loading="isDeactivatingPending"
                    >
                      <v-icon color="grey">mdi-sync</v-icon>
                    </v-btn>
                  </template>

                  <v-btn v-else @click="gotoSale()" :disabled="isStaff" icon>
                    <v-icon color="success">mdi-text-box-check</v-icon>
                  </v-btn>
                </v-fade-transition>

                <order-delete v-model="removeOrder" v-if="!isStaff">
                  <template #activator>
                    <v-btn icon>
                      <v-icon color="error">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </order-delete>
              </div>

              <div class="mx-4">
                <v-data-table
                  :fixed-header="true"
                  :headers="headers"
                  :items="orderItems"
                  item-key="id"
                  class="elevation-1"
                  max-height="65vh"
                  :items-per-page="50"
                  :footer-props="{
                    'items-per-page-options': [50]
                  }"
                >
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      class="showLink"
                      text
                      :ripple="false"
                      :to="{
                        name: 'product',
                        params: { productId: item.item_id },
                      }"
                    >
                      <v-icon color="primary lighten-1">mdi-arrow-expand</v-icon>
                    </v-btn>
                  </template>

                  <template v-slot:item.name="{ item }">
                    <div class="mx-auto" v-text="item.name"></div>
                  </template>

                  <template v-slot:item.price="{ item }">
                    <div class="mx-auto" v-text="item.price"></div>
                  </template>

                  <template v-slot:item.quantity="{ item }">
                    <div class="mx-auto" v-text="item.quantity"></div>
                  </template>

                  <template v-slot:item.total_evaluation="{ item }">
                    <div class="mx-auto" v-text="item.total_evaluation"></div>
                  </template>

                  <template v-slot:item.is_pending="{ item }">
                    <v-fade-transition leave-absolute>
                      <v-icon
                        v-if="!item.is_pending"
                        class="mt-0"
                        color="success"
                      >mdi-check-circle-outline</v-icon>

                      <v-btn
                        v-else
                        :disabled="isStaff"
                        @click="setItemStatus(item.id, item.is_pending)"
                        class="mt-0"
                        icon
                        :loading="isDeactivatingPending"
                      >
                        <v-icon color="grey">mdi-sync</v-icon>
                      </v-btn>
                    </v-fade-transition>
                  </template>

                  <template v-slot:item.delete="{ item }" v-if="!isStaff">
                    <order-product-delete v-model="removeProduct">
                      <template #activator>
                        <v-btn @click="setOrderItemId(item.id)" icon>
                          <v-icon color="error">mdi-close-circle</v-icon>
                        </v-btn>
                      </template>
                    </order-product-delete>
                  </template>
                </v-data-table>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <v-container v-show="isLoadingOrder || orderNotFound">
      <v-row justify="center" align="center">
        <v-fade-transition leave-absolute>
          <v-col cols="auto" v-if="isLoadingOrder">
            <v-progress-circular size="32" color="primary darken-4" indeterminate></v-progress-circular>
          </v-col>

          <v-col v-else cols="auto">
            <v-card color="white" class="pa-8" light elevation="2" :width="cardWidth">
              <div class="d-flex justify-center grey--text">
                <v-icon size="150">mdi-cancel</v-icon>
              </div>

              <div class="text-center title grey--text">
                <span class="font-weight-black">Order</span> not available
              </div>
            </v-card>
          </v-col>
        </v-fade-transition>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import OrderDelete from "@/components/orders/OrderDelete";
import OrderProductDelete from "@/components/orders/OrderProductDelete";

export default {
  name: "Order",
  data() {
    return {
      isLoadingOrder: false,
      removeOrder: null,
      itemId: "",
      removeProduct: null,
      headers: [
        { text: "", value: "actions", sortable: false },
        { text: "Name", align: "center", value: "name" },
        { text: "Unit Price (GH₵)", align: "center", value: "price" },
        { text: "Quantity", align: "center", value: "quantity" },
        { text: "Amount (GH₵)", align: "center", value: "total_evaluation" },
        {
          text: "Status",
          align: "center",
          value: "is_pending",
          sortable: false
        },
        { text: "", align: "center", value: "delete", sortable: false },
        {
          text: "Modified",
          align: "center",
          value: "modified",
          sortable: false
        }
      ],
      updater: null,
      isAddingItems: false,
      isDeactivatingPending: false
    };
  },

  components: {
    OrderDelete,
    OrderProductDelete
  },

  methods: {
    addCartProducts() {
      this.isAddingItems = true;

      setTimeout(() => {
        this.$store.dispatch("updateExistingOrder").then(() => {
          this.isAddingItems = false;
        });
      }, 100);
    },

    async getOrder() {
      this.isLoadingOrder = true;
      var orderId = this.$route.params.orderId;

      await this.$store.dispatch("getOrder", orderId).finally(() => {
        this.isLoadingOrder = false;
      });
    },

    async getOrder2() {
      var orderId = this.$route.params.orderId;

      await this.$store.dispatch("getOrder", orderId);
    },

    gotoSale() {
      this.$store.commit("setNewSaleDialog", true);
    },

    async unsetPendingOrder() {
      var orderId = this.$route.params.orderId;
      this.isDeactivatingPending = true;

      await this.$store.dispatch("readyOrder", orderId).finally(() => {
        this.isDeactivatingPending = false;
      });
    },

    async setItemStatus(itemId, state) {
      var orderId = this.$route.params.orderId;
      this.isDeactivatingPending = true;

      var data = {
        itemId,
        orderId,
        state
      };

      await this.$store.dispatch("setItemStatus", data).finally(() => {
        this.isDeactivatingPending = false;
      });
    },

    async deleteOrder() {
      if (this.removeOrder && this.removeOrder.confirmed) {
        var orderId = this.$route.params.orderId;

        await this.$store
          .dispatch("deleteOrder", orderId)
          .then(() => {
            this.$router.go(-1);
          })
          .catch(() => {});
      }
    },

    setOrderItemId(id) {
      this.itemId = id;
    },

    async deleteProduct() {
      if (this.removeProduct && this.removeProduct.confirmed) {
        var orderId = this.$route.params.orderId;

        await this.$store
          .dispatch("deleteOrderItem", { orderId, itemId: this.itemId })
          .finally(() => {});
      }
    }
  },

  beforeRouteLeave(to, from, next) {
    clearInterval(this.updater);
    next();
  },

  watch: {
    $route() {
      this.getOrder();
    },

    removeOrder: "deleteOrder",
    removeProduct: "deleteProduct"
  },

  mounted() {
    this.getOrder();

    this.updater = setInterval(() => {
      this.getOrder2();
    }, 5000);
  },

  computed: {
    order() {
      return this.$store.state.orders.order;
    },

    orderItems() {
      return this.$store.state.orders.order.order_items;
    },

    orderNotFound() {
      return this.$store.state.orders.orderNotFound;
    },

    pendingOrder() {
      // if (this.$store.state.orders.order.order_items) {
      //   var pending = this.$store.state.orders.order.order_items.filter(
      //     item => item.is_pending === true
      //   );

      //   return pending.length > 0;
      // } else return null;
      return this.$store.state.orders.order.is_pending;
    },

    cardWidth() {
      var name = this.$vuetify.breakpoint.name;

      if (name === "xs") {
        return "99vw";
      } else {
        return "45vw";
      }
    },

    isStaff() {
      if (this.$store.state.user.user_type === "Staff") {
        if (this.$store.state.user.is_privileged) return false;
        return true;
      }
      return false;
    }
  }
};
</script>

<style scoped>
.showLink:hover {
  cursor: pointer;
  text-decoration: underline;
}
</style>
